import { createMachine } from 'xstate';

import getBootstrapGuards from 'common/bento/lib/bootstrap/getBootstrapGuards';
import shared from 'common/bento/shared';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import locales from '../../../locales';
import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',
  HOME = 'home',
  COMPLIANCE_AND_PERSONAL_ADDRESS = 'compliance-and-personal-address',
  POST_COMPLIANCE_AND_PERSONAL_ADDRESS_FINISHED_PROFILE_SEND = 'finish-profile-send',
  PROOF_OF_ADDRESS = 'proof-of-address',
  PROOF_OF_FUNDS = 'proof-of-funds',
  IDENTITY = 'identity',
  QES = 'qes',
  COMPANY_FINANCIAL_INFO = 'company-financial-info',
  COMPANY_PAPERS = 'company-papers',
  STATUTES = 'statutes',
  DRAFT_STATUTES = 'draft-statutes',
  MANDATE = 'mandate',
  BENEFICIARIES_INTRO = 'beneficiaries-intro',
  BENEFICIARIES_LIST = 'beneficiaries-list',
  BENEFICIARIES_REGISTER = 'beneficiaries-register',
  SHINE_START_VERIFICATION = 'shine-start-verification',
  COMPANY_CREATION_DOCS = 'company-creation-docs',
  PRICING_PLAN = 'pricing-plan',
  AGREEMENT = 'agreement',
  DONE = 'done',
  ABORT = 'abort',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'verification',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          // Allows to jump into Identity if the URL includes a param `bootstrap:verification=identity`
          ...getBootstrapGuards([State.IDENTITY, State.QES]),

          {
            actions: Action.TrackVerificationHomeSkipped,
            cond: Guard.ShouldSkipHomeToIdentity,
            target: State.IDENTITY,
          },
          {
            actions: Action.TrackVerificationHomeShowed,
            target: State.HOME,
          },
        ],

        entry: Action.TrackAvoidAppLayoutExperiment,
      },

      [State.HOME]: {
        entry: [
          /**
           * WebView users should never see the Verification Home.
           * The native KYC timeline holds responsability for being the hub at this stage.
           */
          shared.actions.trackView(),
          Action.TrackVerificationHomeErrors,
        ],

        on: {
          GO_AGREEMENT: State.AGREEMENT,
          GO_BENEFICIARIES: State.BENEFICIARIES_INTRO,
          GO_BENEFICIARIES_REGISTER: State.BENEFICIARIES_REGISTER,
          GO_COMPANY_CREATION_DOCS: State.COMPANY_CREATION_DOCS,
          GO_COMPANY_FINANCIAL_INFO: State.COMPANY_FINANCIAL_INFO,
          GO_COMPANY_REGISTRATION: State.COMPANY_PAPERS,
          GO_COMPLIANCE_AND_PERSONAL_ADDRESS:
            State.COMPLIANCE_AND_PERSONAL_ADDRESS,
          GO_DRAFT_STATUTES: State.DRAFT_STATUTES,
          GO_IDENTITY: State.IDENTITY,
          GO_MANDATE: State.MANDATE,
          GO_PRICING_PLAN: State.PRICING_PLAN,
          GO_PROOF_OF_ADDRESS: State.PROOF_OF_ADDRESS,
          GO_PROOF_OF_FUNDS: State.PROOF_OF_FUNDS,
          GO_QES: State.QES,
          GO_SHINE_START_VERIFICATION: State.SHINE_START_VERIFICATION,
          GO_STATUTES: State.STATUTES,
          NEXT: State.DONE,

          PREV: [
            {
              actions: Action.GoToApplicationSelection,
              cond: Guard.IsMandatee,
            },
            {
              actions: shared.actions.toast({
                status: 'error',
                title: locales.toast.definitiveStep,
              }),
            },
          ],
        },
      },

      [State.IDENTITY]: {
        on: {
          NEXT: [
            {
              cond: Guard.IsQesTodo,
              target: State.QES,
            },
            {
              cond: Guard.ShouldSkipHomeToSubmit,
              target: State.DONE,
            },
            State.HOME,
          ],
          PREV: State.HOME,
        },
      },

      [State.QES]: {
        on: {
          NEXT: [
            {
              cond: Guard.ShouldSkipHomeToSubmit,
              target: State.DONE,
            },
            State.HOME,
          ],
          PREV: [
            {
              cond: Guard.IsIdentityReviewInvalid,
              target: State.IDENTITY,
            },
            State.HOME,
          ],
        },
      },

      [State.COMPLIANCE_AND_PERSONAL_ADDRESS]: {
        on: {
          NEXT: [
            {
              actions: Action.TrackApplicantBeneficiaryStatusCompleted,
              cond: Guard.IsComplianceAndPersonalAddressDone,
              target:
                State.POST_COMPLIANCE_AND_PERSONAL_ADDRESS_FINISHED_PROFILE_SEND,
            },
            {
              target: State.HOME,
            },
          ],
          PREV: State.HOME,
        },
      },

      [State.POST_COMPLIANCE_AND_PERSONAL_ADDRESS_FINISHED_PROFILE_SEND]: {
        invoke: {
          id: 'sendHasFinishedProfileSetup',
          onDone: State.HOME,

          onError: {
            actions: shared.actions.error(),
            target: State.HOME,
          },

          src: Service.SendHasFinishedProfileSetup,
        },
      },

      [State.PROOF_OF_ADDRESS]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.PROOF_OF_FUNDS]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.COMPANY_FINANCIAL_INFO]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.COMPANY_PAPERS]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.STATUTES]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.DRAFT_STATUTES]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.MANDATE]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.BENEFICIARIES_INTRO]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.BENEFICIARIES_LIST,
          PREV: State.HOME,
        },
      },

      [State.BENEFICIARIES_LIST]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.HOME,
          PREV: State.BENEFICIARIES_INTRO,
        },
      },

      [State.SHINE_START_VERIFICATION]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.COMPANY_CREATION_DOCS]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.BENEFICIARIES_REGISTER]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.PRICING_PLAN]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.AGREEMENT]: {
        on: {
          NEXT: State.HOME,
          PREV: State.HOME,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
